/**
 * Bills & Reminders Tab
 */

import React, { useState } from 'react';
import { Plus, Loader2, Receipt, Sparkles, Calendar, List } from 'lucide-react';
import { useBillSummary, useBills } from '../../hooks/useBudgetOverview';
import { useIsAiOperational } from '../../hooks/useIsAiOperational';
import { useRegion } from '../../contexts/RegionContext';
import { BillList } from './BillList';
import { BillCalendar } from './BillCalendar';
import { CreateBillModal } from './CreateBillModal';
import { ScanBillsModal } from './scan-bills/ScanBillsModal';
import type { Bill } from '../../types/bill.types';
import clsx from 'clsx';

interface BillsRemindersTabProps {
  onDirtyChange?: (dirty: boolean) => void;
}

type BillsViewMode = 'calendar' | 'list';

export function BillsRemindersTab({ onDirtyChange }: BillsRemindersTabProps): React.ReactElement {
  const { data: summary, isLoading: summaryLoading } = useBillSummary();
  const { data: allBills = [] } = useBills();
  const { formatCurrency } = useRegion();
  const { aiEnabled } = useIsAiOperational();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [editBill, setEditBill] = useState<Bill | null>(null);
  const [viewMode, setViewMode] = useState<BillsViewMode>('calendar');

  const handleCloseModal = () => {
    onDirtyChange?.(false);
    setShowCreateModal(false);
    setEditBill(null);
  };

  const handleEdit = (bill: Bill) => {
    setEditBill(bill);
  };

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-label="Loading bills" />
      </div>
    );
  }

  const isEmpty = allBills.length === 0;

  const scanButton = aiEnabled ? (
    <button
      type="button"
      onClick={() => setShowScanModal(true)}
      className="inline-flex items-center gap-2 px-4 py-2 border border-violet-300 dark:border-violet-600 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors text-sm font-medium"
    >
      <Sparkles className="h-4 w-4" /> Scan for Bills
    </button>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Bills & Reminders</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Keep track of upcoming bills and payments</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {scanButton}
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4" /> Add Bill
            </button>
          </div>
        </div>

        {summary && !isEmpty && (
          <div className="main-page-grid-3-up mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Monthly Bills</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.totalMonthlyBills)}</p>
            </div>
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Due Soon</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{summary.billsDueSoonCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Next 7 days</p>
            </div>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">On Autopay</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{summary.billsOnAutopayCount}</p>
            </div>
          </div>
        )}

        {isEmpty ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/40 mb-4">
              <Receipt className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No bills tracked yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
              Add your recurring bills to get reminders and stay on top of payments.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {aiEnabled && (
                <button
                  type="button"
                  onClick={() => setShowScanModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-violet-300 dark:border-violet-600 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors text-sm font-medium"
                >
                  <Sparkles className="h-4 w-4" /> Scan for Bills
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <Plus className="h-4 w-4" /> Add Your First Bill
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {!isEmpty && (
        <div className="space-y-4">
          <div
            className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-1 bg-white dark:bg-gray-800"
            role="group"
            aria-label="Bills view mode"
          >
            <button
              type="button"
              onClick={() => setViewMode('calendar')}
              className={clsx(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
              aria-pressed={viewMode === 'calendar'}
            >
              <Calendar className="h-4 w-4" /> Calendar
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={clsx(
                'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
              aria-pressed={viewMode === 'list'}
            >
              <List className="h-4 w-4" /> List
            </button>
          </div>

          {viewMode === 'calendar' ? (
            <BillCalendar onEdit={handleEdit} />
          ) : (
            <BillList onEdit={handleEdit} />
          )}
        </div>
      )}

      {(showCreateModal || editBill) && (
        <CreateBillModal
          bill={editBill}
          onClose={handleCloseModal}
          onDirtyChange={onDirtyChange}
        />
      )}

      <ScanBillsModal
        isOpen={showScanModal}
        onClose={() => setShowScanModal(false)}
      />
    </div>
  );
}
